import { LinkIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import type { TabOption } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import type {
  MainTabKey,
  POEContent,
  SectionContent,
} from '@/sections/poe/types/poe.types';

const parseKeyValueProof = (
  poeData: POEContent | string
): Record<string, string> | null => {
  const contentStr =
    typeof poeData === 'string' ? poeData : JSON.stringify(poeData);

  try {
    return JSON.parse(contentStr);
  } catch {
    const lines = contentStr.split('\n');
    const parsed: Record<string, string> = {};
    let hasData = false;
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex > 0) {
        const key = trimmed.substring(0, colonIndex).trim();
        const value = trimmed.substring(colonIndex + 1).trim();
        if (key && value) {
          parsed[key] = value;
          hasData = true;
        }
      }
    }
    return hasData ? parsed : null;
  }
};

export const processSnykCodeDataSource = (
  poeData: POEContent | string,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  mainTabKeys: MainTabKey[]
) => {
  const data = parseKeyValueProof(poeData);
  if (!data) return;

  const vulnerability = data.vulnerability || '';
  const severity = data.severity || '';
  const filePath = data.file || '';
  const lineNumber = data.line || '';
  const cwe = data.cwe || '';
  const cve = data.cve || '';
  const priorityScore = parseFloat(data.priority_score || '0');
  const pkg = data.package || '';
  const manifestFile = data.manifest_file || '';
  const upgradeable = String(data.upgradeable) === 'true';
  const patchable = String(data.patchable) === 'true';
  const remedy = data.remedy || '';
  const description = data.description || '';

  const isOpenSource = !!pkg;
  const lower = severity.toLowerCase();
  const sevColors = lower.includes('critical')
    ? {
        border: 'border-chariot-stroke-critical',
        text: 'text-chariot-stroke-critical',
        bg: 'bg-chariot-status-critical',
      }
    : lower.includes('high')
      ? {
          border: 'border-chariot-stroke-high',
          text: 'text-chariot-stroke-high',
          bg: 'bg-chariot-status-high',
        }
      : lower.includes('medium')
        ? {
            border: 'border-chariot-stroke-medium',
            text: 'text-chariot-stroke-medium',
            bg: 'bg-chariot-status-medium',
          }
        : lower.includes('low')
          ? {
              border: 'border-chariot-stroke-low',
              text: 'text-chariot-stroke-low',
              bg: 'bg-chariot-status-low',
            }
          : {
              border: 'border-chariot-stroke-info',
              text: 'text-chariot-stroke-info',
              bg: 'bg-chariot-status-info',
            };

  if (mainTabKeys.includes('INFORMATION')) {
    sections['snyk_overview'] = (
      <div className="space-y-4">
        <div className={`${sevColors.border} border-l-4 p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ShieldExclamationIcon
                className={`mr-2 size-5 ${sevColors.text}`}
              />
              <h4 className="text-chariot-text-primary font-semibold">
                {vulnerability}
              </h4>
            </div>
            {severity && (
              <span
                className={`${sevColors.bg} ${sevColors.text} rounded-full px-3 py-1 text-xs font-semibold`}
              >
                {severity.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {priorityScore > 0 && (
            <div className="border-chariot-stroke rounded-lg border p-4 text-center">
              <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                Priority Score
              </div>
              <div className={`text-3xl font-bold ${sevColors.text}`}>
                {priorityScore.toFixed(1)}
              </div>
              <div className={`mt-1 text-xs ${sevColors.text}`}>{severity}</div>
            </div>
          )}

          {isOpenSource && (
            <div className="border-chariot-stroke rounded-lg border p-4 text-center">
              <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                Package
              </div>
              <CopyToClipboard alwaysShowCopyIcon textToCopy={pkg}>
                <div className="text-chariot-text-primary text-lg font-bold break-all">
                  {pkg}
                </div>
              </CopyToClipboard>
            </div>
          )}

          {description && (
            <div className="border-chariot-stroke rounded-lg border p-4 md:col-span-2">
              <div className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wider uppercase">
                Details
              </div>
              <div className="text-chariot-text-primary text-sm leading-relaxed whitespace-pre-wrap">
                {description}
              </div>
            </div>
          )}

          {(cwe || cve) && (
            <div className="border-chariot-stroke rounded-lg border p-4 text-center">
              <div className="text-chariot-text-secondary mb-1 text-xs font-medium tracking-wider uppercase">
                {cve ? 'CVE / CWE' : 'CWE'}
              </div>
              <div className="space-y-1">
                {cve && (
                  <div className="text-chariot-text-primary text-lg font-bold">
                    {cve}
                  </div>
                )}
                {cwe && (
                  <div
                    className={
                      cve
                        ? 'text-chariot-text-secondary text-sm'
                        : 'text-chariot-text-primary text-lg font-bold'
                    }
                  >
                    {cwe}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!isOpenSource && filePath && (
          <div className="border-chariot-stroke rounded-lg border p-4">
            <div className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wider uppercase">
              Location
            </div>
            <CopyToClipboard
              alwaysShowCopyIcon
              textToCopy={lineNumber ? `${filePath}:${lineNumber}` : filePath}
            >
              <div className="text-chariot-text-primary text-sm break-all">
                {filePath}
                {lineNumber && (
                  <div className="text-chariot-text-secondary mt-1 text-xs">
                    at line{' '}
                    <span className="text-chariot-brand-primary font-semibold">
                      {lineNumber}
                    </span>
                  </div>
                )}
              </div>
            </CopyToClipboard>
          </div>
        )}

        {isOpenSource && manifestFile && (
          <div className="border-chariot-stroke rounded-lg border p-4">
            <div className="text-chariot-text-secondary mb-2 text-xs font-medium tracking-wider uppercase">
              Manifest File
            </div>
            <CopyToClipboard alwaysShowCopyIcon textToCopy={manifestFile}>
              <div className="text-chariot-text-primary text-sm break-all">
                {manifestFile}
              </div>
            </CopyToClipboard>
            {(upgradeable || patchable) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {upgradeable && (
                  <span className="border-success/30 bg-success/10 text-success inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium">
                    <CheckCircleIcon className="mr-1.5 size-3" />
                    Upgradeable
                  </span>
                )}
                {patchable && (
                  <span className="border-success/30 bg-success/10 text-success inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium">
                    <CheckCircleIcon className="mr-1.5 size-3" />
                    Patchable
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );

    tempTabs['INFORMATION'].push({
      label: isOpenSource ? 'Dependency Alert' : 'Finding Overview',
      value: 'snyk_overview',
    });
  }

  if (mainTabKeys.includes('DETECTION') && remedy) {
    sections['snyk_remedy'] = (
      <div className="space-y-4">
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-2 font-medium">
            Recommended Fix
          </h5>
          <div className="text-chariot-text-primary text-sm leading-relaxed whitespace-pre-wrap">
            {remedy}
          </div>
        </div>
        {cwe && (
          <div className="flex items-center space-x-2 pt-2">
            <LinkIcon className="text-chariot-text-secondary size-4" />
            <a
              href={`https://cwe.mitre.org/data/definitions/${cwe.replace('CWE-', '')}.html`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-chariot-brand-primary text-sm hover:underline"
            >
              {cwe} Reference
            </a>
          </div>
        )}
        {cve && (
          <div className="flex items-center space-x-2">
            <LinkIcon className="text-chariot-text-secondary size-4" />
            <a
              href={`https://nvd.nist.gov/vuln/detail/${cve}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-chariot-brand-primary text-sm hover:underline"
            >
              {cve} — NVD Details
            </a>
          </div>
        )}
      </div>
    );

    tempTabs['DETECTION'].push({
      label: 'Remediation',
      value: 'snyk_remedy',
    });
  }
};
